import React, { useEffect, Suspense, lazy } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App as CapacitorApp } from '@capacitor/app';
import { useFlightCentsStore } from './store';
import { useSubscriptionStore } from './stores/subscriptionStore';
import { motion } from 'framer-motion';
import { ThemeProvider } from './utils/ThemeContext';
import { DarkModeToggle } from './components/DarkModeToggle';
import { Header } from './components/Header';
import { LandingButton } from './components/LandingButton';
import InstallPrompt from './components/InstallPrompt';
import SiteFooter from './components/SiteFooter';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import SEOHead, { getProductSchema, getOrganizationSchema } from './components/SEOHead';
import { injectJsonLd, generateOrganizationSchema, generateSoftwareSchema } from './utils/seo'; // New import for JSON-LD
import { CookieConsent } from './components/CookieConsent';

// Lazy-loaded pages for code splitting
const Landing = lazy(() => import('./pages/Landing'));
const Setup = lazy(() => import('./pages/Setup'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Milestones = lazy(() => import('./pages/Milestones'));
const Reports = lazy(() => import('./pages/Reports'));
const Calculator = lazy(() => import('./pages/Calculator'));
const Products = lazy(() => import('./pages/Products'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogArticle = lazy(() => import('./pages/BlogArticle'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const Store = lazy(() => import('./pages/Store'));
const AffiliateDashboard = lazy(() => import('./pages/AffiliateDashboard'));
const FlightSchoolFinder = lazy(() => import('./pages/FlightSchoolFinder'));
const BenchmarkingTool = lazy(() => import('./components/BenchmarkingTool').then(m => ({ default: m.BenchmarkingTool })));
const Auth = lazy(() => import('./pages/Auth'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));

type View = 'dashboard' | 'milestones' | 'reports' | 'calculator' | 'products';

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [checking, setChecking] = React.useState(true);

  useEffect(() => {
    import('./lib/supabase').then(({ isAuthenticated }) => {
      isAuthenticated().then(authenticated => {
        if (!authenticated) {
          navigate('/auth', { replace: true });
        }
        setChecking(false);
      });
    });
  }, [navigate]);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="text-sm text-neutral-600 dark:text-neutral-300">Loading...</div>
    </div>;
  }

  return <>{children}</>;
};

const RequireProfile: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile, loading } = useFlightCentsStore();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="text-sm text-neutral-600 dark:text-neutral-300">Loading profile...</div>
    </div>;
  }

  if (!profile) return <Navigate to="/setup" replace />;
  return <>{children}</>;
};

const DefaultRedirect: React.FC = () => {
  const { profile } = useFlightCentsStore();
  return <Navigate to={profile ? '/dashboard' : '/setup'} replace />;
};

const LandingRoute: React.FC = () => {
  const { profile } = useFlightCentsStore();
  const navigate = useNavigate();

  // If profile exists, auto-redirect to dashboard when on landing
  useEffect(() => {
    if (profile) {
      navigate('/dashboard', { replace: true });
    }
  }, [profile, navigate]);

  return (
    <>
      <Suspense fallback={<div className="p-4 text-sm text-neutral-600 dark:text-neutral-300">Loading…</div>}>
        <Landing onGetStarted={() => navigate('/auth')} />
      </Suspense>
      {profile && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          onClick={() => navigate('/dashboard')}
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.95 }}
          className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50
            bg-neutral-900 dark:bg-neutral-700 text-white 
            px-6 py-3 rounded-full font-bold text-sm
            shadow-2xl hover:shadow-3xl transition-all
            flex items-center gap-2 backdrop-blur-xl"
          aria-label="Back to app"
        >
          <span>←</span>
          <span>Back to App</span>
        </motion.button>
      )}
    </>
  );
};

const AppShell: React.FC = () => {
  const { initializeApp } = useFlightCentsStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Initialize app with async Supabase check
    initializeApp().catch(console.error);

    // Inject SEO Structured Data
    const cleanupOrg = injectJsonLd(generateOrganizationSchema());
    const cleanupApp = injectJsonLd(generateSoftwareSchema());

    return () => {
      cleanupOrg();
      cleanupApp();
    };
  }, [initializeApp]);

  const isLanding = location.pathname === '/';

  const pathToView = (pathname: string): View | null => {
    if (pathname.startsWith('/dashboard')) return 'dashboard';
    if (pathname.startsWith('/milestones')) return 'milestones';
    if (pathname.startsWith('/reports')) return 'reports';
    if (pathname.startsWith('/calculator')) return 'calculator';
    if (pathname.startsWith('/products')) return 'products';
    return null;
  };

  const currentView = pathToView(location.pathname);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const CANONICAL_ORIGIN = 'https://www.flightcents.com';
  const canonicalUrl = `${CANONICAL_ORIGIN}${location.pathname}`;

  // Intercept external links and open in system browser when running natively
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const isExternalUrl = (href: string) => {
      try {
        const url = new URL(href);
        return url.origin !== origin;
      } catch {
        return false;
      }
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const anchor = target ? target.closest('a') as HTMLAnchorElement | null : null;
      if (!anchor) return;

      const href = anchor.getAttribute('href') || '';
      const targetAttr = anchor.getAttribute('target') || '';
      const relAttr = anchor.getAttribute('rel') || '';

      if (href && isExternalUrl(anchor.href)) {
        e.preventDefault();
        // Prefer opening in system browser for external links on native
        Browser.open({ url: anchor.href });
        return;
      }

      // For safety: prevent default behavior for anchors intended to open in new tab
      if (Capacitor.isNativePlatform() && targetAttr === '_blank') {
        e.preventDefault();
        Browser.open({ url: anchor.href });
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [origin]);

  // Handle Android hardware back button to navigate history or exit app
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listener: any;

    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        // Use browser history to navigate back within the app
        window.history.back();
      } else {
        // If no history, exit the app
        CapacitorApp.exitApp();
      }
    }).then((sub) => {
      listener = sub;
    });

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, []);

  const seoByPath = (pathname: string) => {
    const baseImage = `${CANONICAL_ORIGIN}/images/pilot-training.jpg`;
    switch (true) {
      case pathname === '/':
        return {
          title: 'FlightCents — Flight Training Cost Tracker & Calculator',
          description: 'Track flight training expenses, milestones, and estimate PPL costs with an intuitive calculator. Free, privacy-friendly budgeting for student pilots.',
          keywords: 'flight training cost tracker, PPL calculator, pilot budget app, aviation expenses',
          schema: getOrganizationSchema(),
        };
      case pathname.startsWith('/contact'):
        return {
          title: 'Contact — FlightCents Support & Feedback',
          description: 'Get help with budgeting, milestones, reports, and the PPL calculator. Community links and setup guidance.',
          keywords: 'contact flightcents, pilot budgeting support, training app help',
        };
      case pathname.startsWith('/privacy'):
        return {
          title: 'Privacy Policy — FlightCents Data & Analytics',
          description: 'Learn how FlightCents stores data locally and uses analytics to improve the product. No cloud sync.',
          keywords: 'privacy policy, local storage, analytics, pilot app',
        };
      case pathname.startsWith('/faq'):
        return {
          title: 'FAQ — FlightCents',
          description: 'Answers about setup, dashboard, milestones, reports, and the PPL cost calculator for student pilots.',
          keywords: 'pilot app faq, flight training questions, budgeting answers',
        };
      case pathname.startsWith('/pricing'):
        return {
          title: 'Pricing — FlightCents',
          description: 'FlightCents core budgeting app is free today. Pro features coming soon.',
          keywords: 'flightcents pricing, pilot budgeting app pricing',
        };
      case pathname.startsWith('/blog'):
        return {
          title: 'Blog — Flight Training Budgeting Guides',
          description: 'Articles on realistic PPL costs, budgeting workflows, and training milestone planning.',
          keywords: 'pilot blog, flight training costs, aviation budgeting guides',
        };
      case pathname.startsWith('/setup'):
        return {
          title: 'Setup — FlightCents Profile & Budget Preferences',
          description: 'Create your FlightCents profile to personalize budgeting, milestones, and reports for your training journey.',
          keywords: 'pilot setup, training budget preferences, flight school profile',
        };
      case pathname.startsWith('/dashboard'):
        return {
          title: 'Dashboard — Track Flight Training Budget & Progress',
          description: 'Monitor spending, flight hours, and cost per hour. Visual dashboards for aviation training budgets and progress.',
          keywords: 'pilot dashboard, flight hours, cost per hour, training budget',
        };
      case pathname.startsWith('/milestones'):
        return {
          title: 'Milestones — Flight Training Progress Tracker',
          description: 'Track training milestones like first solo, cross-country, night requirements, written, and checkride with status and costs.',
          keywords: 'pilot milestones, flight training progress, checkride, written exam',
        };
      case pathname.startsWith('/reports'):
        return {
          title: 'Reports — Expense Breakdown, Category Analytics & Export',
          description: 'View expense breakdown by category with charts, export CSV/PDF, and share budget insights.',
          keywords: 'pilot expense reports, CSV export, PDF export, aviation budget analytics',
        };
      case pathname.startsWith('/calculator'):
        return {
          title: 'Cost Calculator — Estimate Private Pilot License (PPL) Costs',
          description: 'Estimate realistic PPL training costs by region, scenario, and custom rates. Shareable links and detailed PDF reports included.',
          keywords: 'PPL cost calculator, flight training estimate, CFI rate, aircraft rental',
          schema: getProductSchema(),
        };
      case pathname.startsWith('/products'):
        return {
          title: 'Recommended Gear — Best Headsets, iPad & Training Tools',
          description: 'Curated list of training gear for student pilots: headsets, ForeFlight/iPad, and accessories to optimize learning.',
          keywords: 'pilot headset, ForeFlight iPad kit, student pilot gear',
        };
      case pathname.startsWith('/affiliate'):
        return {
          title: 'Student Pilot Partnerships — Exclusive Aviation Discounts',
          description: 'Exclusive deals and discounts from trusted aviation partners. Save money on flight training with verified student discounts.',
          keywords: 'student pilot discounts, aviation partnerships, flight training deals, pilot gear discounts',
        };
      case pathname.startsWith('/store'):
        return {
          title: 'Student Pilot Store — Flight Training Essentials',
          description: 'Exclusive deals on flight training essentials including ground school, pilot gear, and training materials.',
          keywords: 'pilot store, flight training gear, student pilot essentials, aviation products',
        };
      case pathname.startsWith('/affiliate-dashboard'):
        return {
          title: 'Affiliate Dashboard — Partnership Performance',
          description: 'Track affiliate earnings, clicks, conversions, and partnership performance metrics.',
          keywords: 'affiliate dashboard, partnership tracking, earnings analytics',
        };
      case pathname.startsWith('/benchmark'):
        return {
          title: 'Flight Training Benchmark Tool — Anonymous Cost Data',
          description: 'Contribute anonymous flight training cost data to help improve regional benchmarking accuracy for student pilots.',
          keywords: 'flight training benchmark, anonymous cost data, regional flight school prices, pilot training database',
        };
      case pathname.startsWith('/schools'):
        return {
          title: 'Flight School Finder — Compare Local Training Costs',
          description: 'Search flight schools by location, aircraft type, rental type, and price range using community-sourced cost data.',
          keywords: 'flight school finder, compare flight school prices, aircraft rental rates, instructor rate, Cessna 172 hourly rate',
        };
      default:
        return {
          title: 'FlightCents — Flight Training Cost Tracker & Calculator',
          description: 'Track expenses and estimate training costs for student pilots. Mobile-first, privacy-friendly budgeting tool.',
          keywords: 'pilot budgeting app, flight training expenses, aviation cost tracker',
          schema: getOrganizationSchema(),
        };
    }
  };

  const seo = seoByPath(location.pathname);

  return (
    <div className="relative min-h-screen">
      <SEOHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        schema={seo.schema}
        canonicalUrl={canonicalUrl}
        imageUrl={`${CANONICAL_ORIGIN}/images/pilot-training.jpg`}
      />
      <Routes>
        <Route path="/" element={<LandingRoute />} />
        <Route path="/setup" element={<RequireAuth><Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Setup /></Suspense></RequireAuth>} />
        <Route path="/contact" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Contact /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><PrivacyPolicy /></Suspense>} />
        <Route path="/faq" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><FAQ /></Suspense>} />
        <Route path="/pricing" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Pricing /></Suspense>} />
        <Route path="/blog" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Blog /></Suspense>} />
        <Route path="/blog/:slug" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><BlogArticle /></Suspense>} />
        <Route path="/dashboard" element={<RequireAuth><RequireProfile><Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Dashboard /></Suspense></RequireProfile></RequireAuth>} />
        <Route path="/milestones" element={<RequireAuth><RequireProfile><Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Milestones /></Suspense></RequireProfile></RequireAuth>} />
        <Route path="/reports" element={<RequireAuth><RequireProfile><Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Reports /></Suspense></RequireProfile></RequireAuth>} />
        <Route path="/calculator" element={<RequireAuth><RequireProfile><Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Calculator /></Suspense></RequireProfile></RequireAuth>} />
        <Route path="/products" element={<RequireAuth><RequireProfile><Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Products /></Suspense></RequireProfile></RequireAuth>} />
        <Route path="/affiliate" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Affiliate /></Suspense>} />
        <Route path="/store" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Store /></Suspense>} />
        <Route path="/affiliate-dashboard" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><AffiliateDashboard /></Suspense>} />
        <Route path="/benchmark" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><BenchmarkingTool /></Suspense>} />
        <Route path="/schools" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><FlightSchoolFinder /></Suspense>} />
        <Route path="/auth" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><Auth /></Suspense>} />
        <Route path="/auth/callback" element={<Suspense fallback={<div className="p-4 text-sm">Loading…</div>}><AuthCallback /></Suspense>} />
        <Route path="*" element={<DefaultRedirect />} />
      </Routes>

      {/* Show site footer on landing and public pages. Hide when in core app views. */}
      {!currentView && <SiteFooter />}

      {!isLanding && (
        <>
          <LandingButton onClick={() => navigate('/')} />
          <DarkModeToggle />
          <InstallPrompt />
        </>
      )}
      <CookieConsent />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
